/**
 * Passkeys (WebAuthn), against the HealthPass backend.
 *
 * The previous version of this file posted to
 * `${KEYCLOAK_URL}/realms/{realm}/webauthn/register` and `/webauthn/authenticate`.
 * Keycloak has no such endpoints - its WebAuthn support only runs inside its own
 * browser flow, which renders its own login pages. Those calls could only ever
 * have 404'd, which is why passkey login was never reachable.
 *
 * The ceremony now runs against our own origin and our own backend, which is
 * also where the credentials are stored. Keycloak still issues every token.
 */

import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

import api from "./api";
import apiClient from "./api/apiClient";

/** The backend's own message for a failure, when it sent one. */
function messageOf(error: unknown): string | undefined {
  const response = (error as { response?: { data?: unknown } })?.response;
  const body = response?.data as { error?: { message?: string } } | undefined;
  return body?.error?.message;
}

export interface PasskeySummary {
  credentialId: string;
  label: string;
  deviceType: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface PasskeyState {
  passkeys: PasskeySummary[];
  /**
   * Derived by the backend from the same rule the login flow uses.
   *
   * "none" is the common case: the emailed code is opt-in, so an account with
   * no passkey usually has no second factor at all.
   */
  secondFactor: "passkey" | "email" | "none";
  emailAddress: string | null;
}

/**
 * A cancelled prompt is not a failure worth reporting as one - the user closed
 * the sheet. Told apart by name, because the message differs per browser.
 */
export function isUserCancellation(error: unknown): boolean {
  const name = (error as { name?: string } | null)?.name;
  return name === "NotAllowedError" || name === "AbortError";
}

class PasskeyService {
  isSupported(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.PublicKeyCredential === "function"
    );
  }

  /** Touch ID, Face ID, Windows Hello - as opposed to a security key. */
  async isPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!this.isSupported()) return false;
    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  }

  async list(): Promise<PasskeyState> {
    const response = await api.get("/auth/passkeys");
    return response.data.data as PasskeyState;
  }

  /**
   * Enrolment, for a user who is already signed in.
   *
   * Two round-trips because the challenge has to come from the server: a
   * challenge the client chose would prove nothing.
   */
  async enrol(label: string): Promise<PasskeySummary> {
    if (!this.isSupported()) {
      throw new Error("This browser does not support passkeys");
    }

    const start = await api.post("/auth/passkeys/register/start");
    const options = start.data.data as PublicKeyCredentialCreationOptionsJSON;

    const attestation: RegistrationResponseJSON = await startRegistration({
      optionsJSON: options,
    });

    const finish = await api.post("/auth/passkeys/register/finish", {
      response: attestation,
      label,
    });

    return finish.data.data as PasskeySummary;
  }

  async remove(credentialId: string): Promise<void> {
    await api.delete(`/auth/passkeys/${encodeURIComponent(credentialId)}`);
  }

  /**
   * Signing in with a passkey and no password at all.
   *
   * Three steps and no account name: the backend issues a challenge without
   * knowing who is about to sign in, the authenticator offers whatever
   * credentials it holds for this site (a discoverable credential), and the
   * one that signs identifies the account. What comes back is the email the
   * passkey belongs to and a single-use proof, which the caller hands to the
   * ordinary login endpoint in place of a password.
   *
   * The proof is not a session. It is spent inside Keycloak's own flow, which
   * is what issues the tokens.
   */
  async signIn(): Promise<{ email: string; passkeyProof: string }> {
    if (!this.isSupported()) {
      throw new Error("This browser does not support passkeys");
    }

    // apiClient rather than a bare fetch: the login page reports its own
    // errors, so this is the client that stays quiet. The two calls here used
    // to be raw fetches that set a JSON content type on a request with no body,
    // which Fastify rejects outright - the same defect the shared client now
    // prevents, and the reason the button did nothing on production.
    const started = await apiClient
      .post("/auth/passkeys/login/start")
      .catch((error: unknown) => {
        throw new Error(
          messageOf(error) ?? "Signing in with a passkey is unavailable.",
        );
      });

    const { handle, options } = started.data.data as {
      handle: string;
      options: PublicKeyCredentialRequestOptionsJSON;
    };
    const assertion = await startAuthentication({ optionsJSON: options });

    const finished = await apiClient
      .post("/auth/passkeys/login/finish", { handle, response: assertion })
      .catch((error: unknown) => {
        throw new Error(
          messageOf(error) ?? "That passkey could not be used to sign in.",
        );
      });

    return finished.data.data as { email: string; passkeyProof: string };
  }

  /**
   * Login half: signs the challenge the backend issued after the password was
   * accepted. Returns the assertion for the caller to hand back - this service
   * never talks to Keycloak itself.
   */
  async sign(
    options: PublicKeyCredentialRequestOptionsJSON,
  ): Promise<AuthenticationResponseJSON> {
    if (!this.isSupported()) {
      throw new Error("This browser does not support passkeys");
    }
    return startAuthentication({ optionsJSON: options });
  }
}

export const passkeyService = new PasskeyService();
export default passkeyService;
